/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import { ALARM_SEVERITIES, AlarmSeverity, normalizeAlarmSeverity } from "./AlarmColors";
import { AlarmData } from "./Types";

export const bucketAlarmsByTime = (alarms: AlarmData[]): { [key: string]: AlarmData[] } => {
  const buckets: { [key: string]: AlarmData[] } = {};

  alarms.forEach((alarm) => {
    const date = new Date(alarm.date);
    const key = date.toISOString();

    if (buckets[key]) {
      buckets[key].push(alarm);
    } else {
      buckets[key] = [alarm];
    }
  });

  return buckets;
};

export const sortAlarmsAlphabetically = (alarms: AlarmData[]) => {
  const alphabeticallySortedAlarms = alarms.sort((a, b) =>
    a.name.localeCompare(b.name, navigator.languages[0] || navigator.language, {
      numeric: true,
      ignorePunctuation: true,
    }),
  );

  return alphabeticallySortedAlarms;
};

export const sortAlarmsByResolved = (alarms: AlarmData[]) => {
  const resolvedAlarms = alarms.filter((alarm) => alarm.stop_time);
  const unresolvedAlarms = alarms.filter((alarm) => !alarm.stop_time);

  return [...unresolvedAlarms, ...resolvedAlarms];
};

export const sortAlarms = (alarms: AlarmData[]) => {
  if (alarms.length === 0) return [];

  // Newest first within a severity bucket (Critical > High > Medium > Low).
  const timeSortedAlarms = alarms.sort((a, b) => {
    return new Date(b.date).valueOf() - new Date(a.date).valueOf();
  });

  let alarmsSecondBucketed = bucketAlarmsByTime(timeSortedAlarms);

  for (const key in alarmsSecondBucketed) {
    alarmsSecondBucketed[key] = sortAlarmsAlphabetically(alarmsSecondBucketed[key]);
  }

  let sortedAlarms = [];
  for (const key in alarmsSecondBucketed) {
    sortedAlarms.push(...alarmsSecondBucketed[key]);
  }

  const resolveSortedAlarms = sortAlarmsByResolved(sortedAlarms);

  return resolveSortedAlarms;
};

/**
 * Split alarms into one sorted bucket per severity, most severe first, and
 * drop the empty buckets. Unrecognized severities land in the default bucket
 * rather than being filtered out, so an alarm is never silently hidden.
 */
export const groupAlarmsBySeverity = (
  alarms?: AlarmData[],
): { severity: AlarmSeverity; alarms: AlarmData[] }[] =>
  ALARM_SEVERITIES.map((severity) => ({
    severity,
    alarms: sortAlarms(
      (alarms ?? []).filter((alarm) => normalizeAlarmSeverity(alarm.alarm_type) === severity),
    ),
  })).filter((group) => group.alarms.length > 0);

export const sortAlarmsBySeverity = (alarms?: AlarmData[]) =>
  groupAlarmsBySeverity(alarms).flatMap((group) => group.alarms);

export const getDiagnosticAlarm = (alarms?: AlarmData[]) =>
  alarms?.find((alarm) => {
    const n = (alarm.name || "").toLowerCase();
    return (
      n.includes("diagnostic_state") ||
      n.includes("diagnostic state") ||
      n.includes("diagnostic_status") ||
      n.includes("diagnostic status")
    );
  });

export const getDataUnavailableAlarm = (alarms?: AlarmData[]) =>
  alarms?.find((alarm) => (alarm.name || "").toLowerCase().includes("data unavailable"));

export const getStatusAlarm = (
  alarms?: AlarmData[],
): { label: string; alarm: AlarmData | null; type: string } | null => {
  const diagnostic = getDiagnosticAlarm(alarms);
  if (diagnostic) return { label: "Disconnected", alarm: diagnostic, type: "diagnostic" };

  const unavailable = getDataUnavailableAlarm(alarms);
  if (unavailable) return { label: "Data Unavailable", alarm: unavailable, type: "unavailable" };

  return null;
};
